/** @format */

import { useState, useEffect, useCallback } from "react";
import {
  collection,
  doc,
  getDocs,
  addDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
  limit,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase/config";

const useUserEnrollment = (courseId) => {
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch students enrolled in this course using courseEnrollments collection
  const fetchEnrolledStudents = useCallback(async () => {
    if (!courseId) return;

    setLoading(true);
    setError(null);

    try {
      // Check if we have a courseEnrollments collection
      let enrollments = [];

      try {
        // Try to get from courseEnrollments collection first
        const enrollmentsQuery = query(
          collection(db, "courseEnrollments"),
          where("courseId", "==", courseId),
          where("status", "==", "active"),
          orderBy("enrolledAt", "desc"),
          limit(100) // Limit to prevent excessive reads
        );

        const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
        enrollments = enrollmentsSnapshot.docs.map((doc) => ({
          enrollmentId: doc.id,
          ...doc.data(),
        }));
      } catch (err) {
        // If courseEnrollments doesn't exist, fall back to old method but with limits
        console.log(
          "courseEnrollments collection not found, using fallback method"
        );

        // Get only active students (limit to 100 instead of ALL users)
        const usersQuery = query(
          collection(db, "users"),
          where("status", "==", "active"),
          limit(100)
        );

        const usersSnapshot = await getDocs(usersQuery);
        const enrolledIds = [];

        // Check each user's Courses subcollection for this course
        for (const userDoc of usersSnapshot.docs) {
          try {
            const userCoursesRef = collection(
              db,
              "users",
              userDoc.id,
              "Courses"
            );
            const courseQuery = query(
              userCoursesRef,
              where("course_id", "==", courseId)
            );
            const courseSnapshot = await getDocs(courseQuery);

            if (!courseSnapshot.empty) {
              enrolledIds.push({
                userId: userDoc.id,
                userData: userDoc.data(),
                enrollmentData: courseSnapshot.docs[0].data(),
                enrollmentId: courseSnapshot.docs[0].id,
              });
            }
          } catch (err) {
            // Skip users with no Courses subcollection
            continue;
          }
        }

        enrollments = enrolledIds;
      }

      setEnrolledStudents(enrollments);
    } catch (err) {
      console.error("Error fetching enrolled students:", err);
      setError("Failed to fetch enrolled students");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  // Enroll students in the course
  const enrollStudents = useCallback(
    async (studentIds, expireDate) => {
      setOperationLoading(true);
      setError(null);

      try {
        const batch = writeBatch(db);

        for (const studentId of studentIds) {
          // Add course to student's Courses subcollection
          const userCoursesRef = collection(db, "users", studentId, "Courses");
          const newCourseRef = doc(userCoursesRef);

          batch.set(newCourseRef, {
            course_id: courseId,
            expire_date: expireDate,
            enrolled_at: new Date().toISOString(),
            status: "active",
          });

          // Also add to courseEnrollments collection for efficient querying
          try {
            const courseEnrollmentsRef = collection(db, "courseEnrollments");
            const newEnrollmentRef = doc(courseEnrollmentsRef);

            batch.set(newEnrollmentRef, {
              courseId: courseId,
              userId: studentId,
              expireDate: expireDate,
              enrolledAt: new Date().toISOString(),
              status: "active",
            });
          } catch (err) {
            // If courseEnrollments doesn't exist, skip this
            console.log("courseEnrollments collection not available");
          }
        }

        await batch.commit();

        // Refresh enrolled students list
        await fetchEnrolledStudents();

        return { success: true, enrolledCount: studentIds.length };
      } catch (err) {
        console.error("Error enrolling students:", err);
        setError("Failed to enroll students");
        return { success: false, error: err.message };
      } finally {
        setOperationLoading(false);
      }
    },
    [courseId, fetchEnrolledStudents]
  );

  // Remove student from course
  const unenrollStudent = useCallback(
    async (studentId) => {
      setOperationLoading(true);
      setError(null);

      try {
        // Find and delete the course from student's subcollection
        const userCoursesRef = collection(db, "users", studentId, "Courses");
        const courseQuery = query(
          userCoursesRef,
          where("course_id", "==", courseId)
        );
        const courseSnapshot = await getDocs(courseQuery);

        if (!courseSnapshot.empty) {
          await deleteDoc(courseSnapshot.docs[0].ref);

          // Also remove from courseEnrollments collection
          try {
            const enrollmentsQuery = query(
              collection(db, "courseEnrollments"),
              where("courseId", "==", courseId),
              where("userId", "==", studentId)
            );
            const enrollmentsSnapshot = await getDocs(enrollmentsQuery);

            if (!enrollmentsSnapshot.empty) {
              await deleteDoc(enrollmentsSnapshot.docs[0].ref);
            }
          } catch (err) {
            // If courseEnrollments doesn't exist, skip this
            console.log("courseEnrollments collection not available");
          }

          await fetchEnrolledStudents(); // Refresh list
          return { success: true };
        }

        return { success: false, error: "Enrollment not found" };
      } catch (err) {
        console.error("Error unenrolling student:", err);
        setError("Failed to unenroll student");
        return { success: false, error: err.message };
      } finally {
        setOperationLoading(false);
      }
    },
    [courseId, fetchEnrolledStudents]
  );

  // Bulk unenroll students
  const unenrollStudents = useCallback(
    async (studentIds) => {
      setOperationLoading(true);
      setError(null);

      try {
        const batch = writeBatch(db);

        for (const studentId of studentIds) {
          const userCoursesRef = collection(db, "users", studentId, "Courses");
          const courseQuery = query(
            userCoursesRef,
            where("course_id", "==", courseId)
          );
          const courseSnapshot = await getDocs(courseQuery);

          if (!courseSnapshot.empty) {
            batch.delete(courseSnapshot.docs[0].ref);
          }

          // Also remove from courseEnrollments collection
          try {
            const enrollmentsQuery = query(
              collection(db, "courseEnrollments"),
              where("courseId", "==", courseId),
              where("userId", "==", studentId)
            );
            const enrollmentsSnapshot = await getDocs(enrollmentsQuery);

            if (!enrollmentsSnapshot.empty) {
              batch.delete(enrollmentsSnapshot.docs[0].ref);
            }
          } catch (err) {
            // If courseEnrollments doesn't exist, skip this
            console.log("courseEnrollments collection not available");
          }
        }

        await batch.commit();
        await fetchEnrolledStudents(); // Refresh list

        return { success: true, unenrolledCount: studentIds.length };
      } catch (err) {
        console.error("Error unenrolling students:", err);
        setError("Failed to unenroll students");
        return { success: false, error: err.message };
      } finally {
        setOperationLoading(false);
      }
    },
    [courseId, fetchEnrolledStudents]
  );

  useEffect(() => {
    if (courseId) {
      fetchEnrolledStudents();
    }
  }, [courseId, fetchEnrolledStudents]);

  return {
    enrolledStudents,
    loading,
    operationLoading,
    error,
    enrollStudents,
    unenrollStudent,
    unenrollStudents,
    refreshEnrollments: fetchEnrolledStudents,
  };
};

export default useUserEnrollment;
